
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { PersonalSettingsForm, UserSettings } from './types';

export async function fetchUserSettings(userId: string): Promise<UserSettings | null> {
  try {
    // Fetch from users table instead of user_settings
    const { data, error } = await supabase
      .from('users')
      .select('id, first_name, last_name, email, phone, created_at, updated_at')
      .eq('id', userId)
      .maybeSingle();
    
    if (error) {
      console.error('Error fetching user settings:', error);
      return null;
    }
    
    return data;
  } catch (error) {
    console.error('Error in fetchUserSettings:', error);
    return null;
  }
}

export async function updateUserSettings(
  userId: string,
  settings: PersonalSettingsForm
): Promise<UserSettings | null> {
  try {
    console.log('Updating user settings for userId:', userId);
    
    // Get the current authenticated user to ensure we're using the correct ID
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user || user.id !== userId) {
      console.error('Authentication mismatch: current user does not match target user ID');
      toast.error('Authentication error. Please log in again.');
      return null;
    }
    
    // Update the users table directly
    const { data, error } = await supabase
      .from('users')
      .update({
        first_name: settings.firstName,
        last_name: settings.lastName,
        email: settings.email,
        phone: settings.phone || null
      })
      .eq('id', userId)
      .select()
      .single();
    
    if (error) {
      console.error('Error updating user:', error);
      toast.error('Failed to update personal information');
      return null;
    }
    
    // Also update the auth user email if it changed
    if (settings.email !== user.email) {
      const { error: authError } = await supabase.auth.updateUser({
        email: settings.email
      });
      
      if (authError) {
        console.error('Error updating auth user email:', authError);
        toast.error('Failed to update email address in authentication system');
        // Continue anyway as the profile was updated
      }
    }
    
    toast.success('Personal information updated successfully');
    return data;
  } catch (error) {
    console.error('Error in updateUserSettings:', error);
    toast.error('An unexpected error occurred');
    return null;
  }
}
